import { type ReactNode, useState } from 'react';

import { Box, Col, Row } from '@opendoor/atoms/box';
import { Button } from '@opendoor/atoms/button';
import { Image } from '@opendoor/atoms/image';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import { useIsomorphicLayoutEffect } from 'components/shared/hooks/useIsomorphicLayoutEffect';
import { useRouter } from 'next/router';

import { ListingPartnerDetails } from './ListingPartnerDetails';
import { type OfferConfirmation, readOfferConfirmation } from './offerConfirmationHandoff';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const CARD = 'w-full flex-col gap-4 rounded-[16px] border border-od-border-primary p-4';
const CARD_TITLE = 'font-medium text-[18px] tracking-[-0.432px] text-od-content-primary';

const TimelineItem = ({
  state,
  last = false,
  title,
  detail,
}: {
  state: 'done' | 'current' | 'upcoming';
  last?: boolean;
  title: string;
  detail: string;
}) => (
  <Row className="items-stretch gap-3">
    <Col className="w-5 items-center">
      {state === 'done' ? (
        <IconCircleCheckFilled className="size-5 shrink-0 text-od-content-success" />
      ) : (
        <Box
          className={cn(
            'flex size-5 shrink-0 items-center justify-center rounded-full',
            state === 'current' ? 'bg-od-bg-success/10' : undefined,
          )}
        >
          <Box
            className={cn(
              'size-2.5 rounded-full',
              state === 'current' ? 'bg-od-bg-success' : 'bg-od-bg-inactive-secondary',
            )}
          />
        </Box>
      )}
      {last ? null : <Box className="w-px flex-1 bg-od-border-primary" />}
    </Col>
    <Col className={cn('gap-0.5', last ? undefined : 'pb-4')}>
      <Text className="font-medium text-body-md text-od-content-primary">{title}</Text>
      <Text className="text-body-sm text-od-content-secondary">{detail}</Text>
    </Col>
  </Row>
);

const OfferRow = ({ label, children }: { label: string; children: ReactNode }) => (
  <Row className="items-start justify-between gap-4">
    <Text className="text-body-md text-od-content-secondary">{label}</Text>
    <Text className="text-right font-medium text-body-md text-od-content-primary">{children}</Text>
  </Row>
);

/** The design shows the street on its own line, the city-state-zip below. */
const addressLines = (address: string): string[] => {
  const commaAt = address.indexOf(',');
  if (commaAt === -1) {
    return [address];
  }
  return [address.slice(0, commaAt), address.slice(commaAt + 1).trim()];
};

const OfferConfirmationPage = () => {
  const router = useRouter();
  // Read before the browser paints the new route while keeping the initial
  // render identical to the server HTML for direct visits.
  const [confirmation, setConfirmation] = useState<OfferConfirmation | undefined>(undefined);
  const [checked, setChecked] = useState(false);

  useIsomorphicLayoutEffect(() => {
    const stored = readOfferConfirmation();
    setConfirmation(stored);
    setChecked(true);
    if (!stored) {
      void router.replace('/make-offer/start');
    }
  }, []);

  if (!checked || !confirmation) {
    return <Box className="min-h-[50vh] w-full bg-od-bg-primary" />;
  }

  return (
    <Box as="section" className="w-full justify-center bg-od-bg-primary px-6 pt-14 pb-28">
      <Col className="w-full max-w-[608px] items-center gap-6">
        <Col className="items-center gap-4">
          <Box className="flex size-14 items-center justify-center rounded-[12px] bg-od-bg-success/10">
            <IconCircleCheckFilled className="size-7 text-od-content-success" />
          </Box>
          <Text
            as="h1"
            className="text-center font-medium text-[28px] tracking-[-0.784px] text-od-content-primary"
          >
            We’ve received your offer
          </Text>
          <Text className="text-center text-body-md text-od-content-secondary">
            Your offer is being reviewed, and we’ll respond within 24 hours. You can contact the
            listing partner in the meantime.
          </Text>
        </Col>

        <Row className={cn(CARD, 'flex-row items-center')}>
          <Box
            className={cn(
              'size-[120px] shrink-0 overflow-hidden rounded-[8px]',
              !confirmation.photoUrl && 'bg-od-bg-inactive-secondary',
            )}
          >
            {confirmation.photoUrl ? (
              <Image src={confirmation.photoUrl} alt="" className="size-full object-cover" />
            ) : null}
          </Box>
          <Col className="flex-1 gap-3">
            <OfferRow label="Address">
              {addressLines(confirmation.address).map((line, index) => (
                <Text as="span" key={line} className={cn('block', index > 0 && 'font-normal')}>
                  {line}
                </Text>
              ))}
            </OfferRow>
            <Box className="h-px w-full bg-od-border-primary" />
            <OfferRow label="Offer amount">{usd.format(confirmation.priceCents / 100)}</OfferRow>
          </Col>
        </Row>

        <Col className={CARD}>
          <Text as="h2" className={CARD_TITLE}>
            Next steps
          </Text>
          <Col>
            <TimelineItem
              state="done"
              title="Offer received"
              detail={`Offer #${confirmation.offerId} · Received just now`}
            />
            <TimelineItem
              state="current"
              title="Review the offer"
              detail="Review offer terms, conditions, and supporting documents"
            />
            <TimelineItem
              state="upcoming"
              last
              title="Listing partner will follow up"
              detail="Listing partner will contact you within 24 hours"
            />
          </Col>
        </Col>

        <Col
          as="section"
          aria-label="Listing partner"
          className="w-full min-w-0 rounded-[16px] bg-od-bg-secondary p-4"
        >
          <ListingPartnerDetails partner={confirmation} headingAs="h2" />
        </Col>

        <Col className="w-full gap-3">
          <Button
            href="/make-offer/start"
            variant="secondary"
            className="w-full rounded-[12px]"
            aria-label="Start another offer"
            analyticsName="cosmos-make-offer-confirm-submit-another"
          >
            Start another offer
          </Button>
        </Col>
      </Col>
    </Box>
  );
};

export default OfferConfirmationPage;
